import { Link, useNavigate, useParams } from "react-router-dom";
import { adminProcApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";
import type { SupplierDelisting } from "../../types";

const REASON_LABELS: { key: keyof SupplierDelisting; label: string }[] = [
  { key: "reasonPoorPerformance", label: "Poor Performance" },
  { key: "reasonNonCompliance", label: "Non-Compliance" },
  { key: "reasonFraudulent", label: "Fraudulent Activity" },
  { key: "reasonExpiredDocs", label: "Expired Documents" },
  { key: "reasonLateDelivery", label: "Late Delivery" },
];

export default function DelistingViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: d, loading, error, reload } = useAsyncData(
    () => adminProcApi.getDelisting(Number(id)),
    [id],
  );

  const { mutate: doApprove, loading: approving, error: approveErr } = useMutation(() =>
    adminProcApi.approveDelisting(Number(id)).then(() => reload()),
  );
  const { mutate: doReject, loading: rejecting, error: rejectErr } = useMutation(() =>
    adminProcApi.rejectDelisting(Number(id)).then(() => reload()),
  );

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!d) return null;

  const actionErr = approveErr || rejectErr;
  const activeReasons = REASON_LABELS.filter(({ key }) => d[key]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/admin/delistings" className="text-sm text-emerald-700 hover:underline">&larr; Back to Delistings</Link>
      </div>

      <ProcForm formNo="PROC-FRM-12" title="Supplier Delisting" serialNo={d.serialNo} date={d.reviewDate}>
        <ProcSection title="Delisting Details">
          <ProcGrid>
            <ProcField label="Supplier" value={d.supplierName} />
            <ProcField label="Review Date" value={d.reviewDate ? new Date(d.reviewDate).toLocaleDateString("en-KE") : "—"} />
            <ProcField label="Status" value={<ProcStatusBadge value={d.status} />} />
            <ProcField
              label="Reasons"
              value={activeReasons.length ? activeReasons.map((r) => r.label).join(", ") : "—"}
              span2
            />
            {d.reasonOther && <ProcField label="Other Reason" value={d.reasonOther} span2 />}
            {d.remarks && <ProcField label="Remarks" value={d.remarks} span2 />}
          </ProcGrid>
        </ProcSection>

        {d.status === "PENDING" && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => doApprove()}
                disabled={approving || rejecting}
                className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
              >
                {approving ? "Approving…" : "Approve (Delist Supplier)"}
              </button>
              <button
                type="button"
                onClick={() => doReject()}
                disabled={approving || rejecting}
                className="rounded-xl border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                {rejecting ? "Rejecting…" : "Reject"}
              </button>
            </div>
          </ProcSection>
        )}

        {d.status !== "PENDING" && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate("/procurement/suppliers")}
              className="text-sm text-emerald-700 hover:underline"
            >
              View Supplier Register &rarr;
            </button>
          </div>
        )}
      </ProcForm>
    </div>
  );
}
